import React, { Component } from "react";
import { Button, Grid, Icon, Message } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import styled from 'styled-components';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import Input from 'components/final-form/Input';
import {
  MedicamentDto,
  MedicamentEnumDto,
  WsIndexAddRequest,
  WsIndexAddSearchRequest,
  WsIndexAddSearchResponse
} from 'ts-types/api.types';
import { FormState } from 'final-form';
import { getAllTariffCodes, wsIndexAddMedicaments, wsIndexAddSearchMedicaments } from 'services/medicamentServices';
import axios, { CancelTokenSource } from 'axios';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { MedicamentConsumerRenderProps, withMedicamentContext } from 'components/medicaments/MedicamentsContext';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import LoaderComponent from 'components/LoaderComponent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import moment from 'moment';
import { numberFormatter } from 'utils/fomattingUtils';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import FormContent from 'components/layout/FormContent';
import FormContainer from 'components/layout/FormContainer';
import SearchByButtonsStyle from 'components/layout/SearchByButtonsStyle';
import { isKeyCheck } from 'utils/keyUtils';
import { CheckboxSc } from 'components/final-form/CheckBoxThreeState';
import ConfirmActionPopup from 'components/ConfirmActionPopup';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__row.totalRow {
    font-weight: 700;
    background-color: rgba(0, 0, 0, .2);

    &:hover {
      background-color: rgba(0, 0, 0, .2) !important;
    }
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;


const StyledGrid = styled(Grid)`
  flex: 1 0 200px;
  margin-top: 1rem !important;
`;

const StyledMedicamentsTableContainer = styled.div`
  height: 500px;

  .info-class {
    align-self: baseline;
    padding-top: 4px;
  }
`;

const ButtonsDivider = styled.div`
  margin-top: 2rem;
`;

const StyledCell = styled.div`
  text-align: right;
  padding-right: 0.4rem;
`;

interface MedicamentsFieldProps extends FormState {
  values: Partial<WsIndexAddSearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, MedicamentConsumerRenderProps, NotificationSystemProps {
  onClose: (importing?: boolean) => void
}

interface State {
  foundMedicaments: Array<MedicamentDto>,
  medicamentsLoaded: boolean,
  showNotSelected: boolean,
  tariffCodes: Array<MedicamentEnumDto>,
  selectedTariffCode?: string | undefined,
  searchBy: string,
  noMedicamentsFoundMessage?: string,
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string
}

class WSIndexAddView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundMedicaments: [],
      medicamentsLoaded: true,
      showNotSelected: false,
      tariffCodes: [],
      searchBy: "description",
      selectedTariffCode: undefined,
      cancelTokenSource
    };

    getAllTariffCodes(cancelTokenSource)
        .then(response => {
          this.setState({
            tariffCodes: response
          })
        })
        .catch(this.handleError);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  searchByCriteria = (criteria: string) => {
    this.setState({
      searchBy: criteria
    });
  };

  initialFormValues = () => {
    const {currentUser} = this.props;
    return {
      mandateId: currentUser!.userConfig.mandateId
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  }

  setNoMedicamentsFoundMessage = (message?: string) => {
    this.setState({
      noMedicamentsFoundMessage: message
    })
  }

  handleError = () => {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage("error.general"));
  }

  triggerSearchOnEnter(values:any, event: any, source?: string) {
    event.persist();

    const isKey: any = isKeyCheck(event);

    if (isKey.enter) {
      this.handleSearch(values);
    }
  }

  handleSubmit = (values: any) => {
    const {selectionNotificationSystem, onClose, getMessage, getMessageWithNamedParams} = this.props;
    const {foundMedicaments, cancelTokenSource} = this.state;

    const selectedMedicaments = foundMedicaments.filter(m => m.selection);
    const pharmaCodes: Array<string> = selectedMedicaments.map(m => m.pharmaCode);

    const request: WsIndexAddRequest = {
      mandateId: values.mandateId,
      pharmaCodes: pharmaCodes
    }

    const onFinally = () => {
      onClose(true);
    }

    wsIndexAddMedicaments(request, cancelTokenSource)
        .then(response => {
          //@ts-ignore
          const notification = selectionNotificationSystem.current;
          const medicamentsSize = response.medicaments.length;
          const errorsSize = response.errorMessage.length;

          const successfullyImportedMessage = getMessageWithNamedParams(
              "medicamentUpdate.successfulImport",
              {numberOfImported: medicamentsSize});
          const notImportedMessage = getMessageWithNamedParams(
              "medicamentUpdate.notImported",
              {numberOfErrors: errorsSize});
          const partiallyImported = <span>{successfullyImportedMessage} <br/> {notImportedMessage}</span>;

          if (notification) {
            if (errorsSize == 0) {
              notification.addNotification({
                title: getMessage("medicament.update.success"),
                message: successfullyImportedMessage,
                level: 'success'
              });
            } else if (errorsSize > 0 && medicamentsSize > 0) {
              notification.addNotification({
                title: getMessage("medicament.update.success"),
                message: partiallyImported,
                level: 'warning'
              });
            } else {
              notification.addNotification({
                title: getMessage("error.header"),
                message: notImportedMessage,
                level: 'error'
              });
            }

            const medicaments = response.medicaments;
            this.setState({
              foundMedicaments: medicaments
            });
          }
        })
        .catch(this.handleError)
        .finally(onFinally);
  };

  handleSearch = (values: any): void => {
    if (!this.mounted) {
      return;
    }

    const {currentUser, getMessage} = this.props;
    const {searchBy, noMedicamentsFoundMessage, cancelTokenSource} = this.state;

    if (noMedicamentsFoundMessage) {
      this.setNoMedicamentsFoundMessage(undefined);
    }

    this.setState({
      medicamentsLoaded: false
    });

    const onFinally = () => {
      this.setState({
        medicamentsLoaded: true
      })
    };

    const request: Partial<WsIndexAddSearchRequest> = {
      mandateId: currentUser!.userConfig.mandateId,
      description: searchBy === "description" && values.description ? values.description : undefined,
      pharmaCode: searchBy === "pharmaCode" && values.pharmaCode ? values.pharmaCode : undefined
    }

    wsIndexAddSearchMedicaments(request, cancelTokenSource)
        .then((response: WsIndexAddSearchResponse) => {
          let foundMedicaments = response.medicaments;

          this.setState({
            foundMedicaments: foundMedicaments
          });

          if (foundMedicaments.length == 0) {
            const message = getMessage("medicamentUpdate.noMedicamentsFound.message");

            this.setNoMedicamentsFoundMessage(message)
          }
        })
        .catch(this.handleError)
        .finally(onFinally);
  }

  render(): React.ReactNode {
    return (
        <React.Fragment>
          {this.renderSearchMedicamentSegment()}
        </React.Fragment>
    );
  }

  renderSearchMedicamentSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialFormValues()}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {
    const {onClose, getMessage} = this.props;

    const {foundMedicaments, medicamentsLoaded, errorMessage, searchBy, noMedicamentsFoundMessage} = this.state;

    let disableSearch = true;

    const searchDescription = searchBy === "description" && values.description && values.description.length > 0;
    const searchPharmaCode = searchBy === "pharmaCode" && values.pharmaCode && values.pharmaCode.length > 0;

    if (searchDescription || searchPharmaCode) {
      disableSearch = false;
    }

    const countSelectedMeds = foundMedicaments.filter(m => m.selection).length;

    const selectedMedicamentWithOlderPrices = foundMedicaments.filter(m => m.selection && m.olderPrices).length;

    return (
        <FormContainer>
          <FormContent>
            <PraxFormGrid>
              {errorMessage
              && (
                  <Grid.Row>
                    <Grid.Column>
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                    </Grid.Column>
                  </Grid.Row>
              )
              }
              <Grid.Row>
                <Grid.Column width={4} verticalAlign="middle">
                  {getMessage(`${searchBy}`)}:
                </Grid.Column>
                <Grid.Column width={searchBy === "pharmaCode" ? 4 : 9}>
                  <Field
                      name={searchBy}
                      component={Input}
                      fluid
                      autoFocus
                      onKeyDown={(e: any) => this.triggerSearchOnEnter(values, e, 'search')}
                  />
                </Grid.Column>
                <Grid.Column width={searchBy === "pharmaCode" ? 8 : 3} textAlign="right">
                  <Button type="button" primary compact
                          onClick={() => this.handleSearch(values)}
                          disabled={disableSearch}
                  >
                    {getMessage("search")}
                  </Button>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={4} verticalAlign="middle">
                  {getMessage("medicament.search.by")}:
                </Grid.Column>
                <Grid.Column width={6}>
                  <SearchByButtonsStyle compact size="tiny">
                    <Button primary={searchBy === 'pharmaCode' && true}
                            basic={searchBy === 'pharmaCode' ? undefined : true}
                            onClick={() => this.searchByCriteria("pharmaCode")}
                    >
                      {getMessage("pharmaCode")}
                    </Button>
                    <Button primary={searchBy === 'description' && true}
                            basic={searchBy === 'description' ? undefined : true}
                            onClick={() => this.searchByCriteria("description")}
                    >
                      {getMessage("description")}
                    </Button>
                  </SearchByButtonsStyle>
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
          </FormContent>
          <StyledGrid>
            <Grid.Row>
              <Grid.Column width={16}>
                {noMedicamentsFoundMessage &&
                    <Message color="yellow" onDismiss={() => this.setNoMedicamentsFoundMessage()}>
                      {noMedicamentsFoundMessage}
                    </Message>
                }

                <StyledMedicamentsTableContainer>
                  {!medicamentsLoaded ?
                      <LoaderComponent message={getMessage("medicament.loading")}/> :
                      this.renderMedicamentsSegment()}
                </StyledMedicamentsTableContainer>
              </Grid.Column>
            </Grid.Row>
          </StyledGrid>
          <ButtonsDivider>
            {selectedMedicamentWithOlderPrices > 0
                ? <ConfirmActionPopup
                    renderTarget={
                      <Button compact
                              type="submit"
                              primary
                              disabled={!countSelectedMeds}
                      >
                        {getMessage("import")}
                      </Button>
                    }
                    onConfirmAction={() => this.handleSubmit(values)}
                    message={getMessage('medicamentUpdate.import.confirmMessage')}
                    position="top left"
                />
                : <Button compact
                          type="submit"
                          primary
                          onClick={() => this.handleSubmit(values)}
                          disabled={!countSelectedMeds}
                >
                  {getMessage("import")}
                </Button>
            }
            <Button compact type="button" secondary onClick={() => onClose(false)}>
              {getMessage("button.close")}
            </Button>
          </ButtonsDivider>
        </FormContainer>
    )
  };

  selectMedicament = (index: number) => {
    const {foundMedicaments} = this.state;

    let editMedicaments = [...foundMedicaments];
    let medicament = editMedicaments[index];

    if (medicament) {
      medicament.selection = !medicament.selection;
      this.setState({
        foundMedicaments: editMedicaments
      })
    }
  };

  foundMedicamentsRowGetter = ({index}: any) => {
    const {foundMedicaments} = this.state;
    return foundMedicaments[index];
  };

  foundMedicamentsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundMedicaments} = this.state;
    const medicament = foundMedicaments[index];
    const differenceFound = !medicament.differenceFound && medicament.id;

    if (medicament.id) {
      style.color = '#000';
    } else if (!medicament.selection ) {
      style.color = '#c6c6cb';
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => !differenceFound ? this.selectMedicament(index) : {}}
        >
          {columns}
        </div>
    );
  };

  effectiveDateCellRenderer = (data: any) => {
    if (data.rowData.effectiveDate === null) {
      return <div></div>;
    } else {
      return <div>{moment(data.rowData.effectiveDate).format("DD.MM.YYYY")}</div>;
    }
  }

  billingPriceCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const billingPriceUpdated = rowData.billingPriceUpdated;

    if (cellData) {
      return (
          <div style={
            billingPriceUpdated
                ? {display: "flex", justifyContent: "flex-end"}
                : {}
          }>
            {billingPriceUpdated &&
                <Icon name="info circle"
                      color="blue"
                      title={getMessage("medicamentUpdate.differentBillingPrice")}
                />
            }
            <StyledCell>{numberFormatter.format(cellData)}</StyledCell>
          </div>
      );
    }

    return (
        <div></div>
    );
  }

  doctorsPriceCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const doctorsPriceUpdated = rowData.doctorsPriceUpdated;

    if (cellData) {
      return (
          <div style={
            doctorsPriceUpdated
              ? {display: "flex", justifyContent: "flex-end"}
              : {}
          }>
            {doctorsPriceUpdated &&
                <Icon name="info circle"
                      color="blue"
                      title={getMessage("medicamentUpdate.differentDoctorsPrice")}
                />
            }
            <StyledCell>{numberFormatter.format(cellData)}</StyledCell>
          </div>
      );
    }

    return (
        <div></div>
    );
  }

  medicamentSelectCellRenderer = (data: any) => {

    const {getMessage} = this.props;
    const {rowIndex, rowData, cellData} = data;

    const differenceFound = !rowData.differenceFound && rowData.id;

    return (
        <div style={{display: "flex"}}>
          <CheckboxSc
              checked={cellData}
              onChange={() => this.selectMedicament(rowIndex)}
              style={{alignSelf: "end"}}
              disabled={differenceFound}
          />
          {differenceFound &&
              <Icon name="info circle"
                    color="blue"
                    className="info-class"
                    title={getMessage("medicamentUpdate.noChangesFound")}
              />
          }
          {rowData.olderPrices &&
              <Icon name="warning sign"
                    color="yellow"
                    className="info-class"
                    title={getMessage("medicamentUpdate.olderPrices")}
              />
          }
        </div>
    )
  };

  renderMedicamentsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundMedicaments} = this.state;

    return (
        <StyledTable
            rowCount={foundMedicaments.length}
            rowGetter={this.foundMedicamentsRowGetter}
            rowRenderer={this.foundMedicamentsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 150,
                label: (getMessage("select")),
                dataKey: "selection",
                cellRenderer: this.medicamentSelectCellRenderer
              },
              {
                width: 200,
                label: (getMessage("code")),
                dataKey: "code"
              },
              {
                width: 200,
                label: (getMessage("pharmaCode")),
                dataKey: "pharmaCode"
              },
              {
                width: 900,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 170,
                label: (getMessage("price")),
                dataKey: "billingPrice",
                cellRenderer: this.billingPriceCellRenderer
              },
              {
                width: 170,
                label: (getMessage("doctorsPrice")),
                dataKey: "doctorsPrice",
                cellRenderer: this.doctorsPriceCellRenderer
              },
              {
                width: 220,
                label: `${getMessage("from")} ${getMessage("date")}`,
                dataKey: "effectiveDate",
                cellRenderer: this.effectiveDateCellRenderer
              }
            ]}
        />
    );
  };

}

const WSIndexAddViewWrapper =
    withMessages(
        withAuthContext(
            withMedicamentContext(
                withNotificationSystemContext(
                    WSIndexAddView))));


export default WSIndexAddViewWrapper;
